import React, { useState, useEffect } from "react";
import { createProductTypeApi } from "../../../../api/ProductTypeList";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Modal, Button } from "react-bootstrap";

function normalizeString(inputString) {
  const stringWithoutDiacritics = inputString
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const stringWithoutD = stringWithoutDiacritics.replace(/đ/g, "d");
  const normalizedText = stringWithoutD.replace(/\s+/g, "").toLowerCase();
  return normalizedText;
}

const CreateProductType = () => {
  const [name, setName] = useState("");
  const [parentCategoryID, setParentCategoryID] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [nameError, setNameError] = useState("");
  const [show, setShow] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/categories/getCategoryID`
      );
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setNameError("");
    setErrorMessage("");
  };

  const handleParentCategoryChange = (e) => {
    setParentCategoryID(e.target.value);
  };

  const handleCreate = async () => {
    setIsLoading(true);
    try {
      let hasError = false;

      if (!name.trim()) {
        setNameError("Vui lòng điền Tên Loại Sản Phẩm.");
        hasError = true;
      }

      if (hasError) {
        setErrorMessage("Vui lòng điền đầy đủ thông tin.");
        setSuccessMessage("");
        setIsLoading(false);
        return;
      }

      const newProductType = {
        categoryID: normalizeString(name.trim()),
        name: name.trim(),
        parentCategoryID: parentCategoryID || undefined,
      };

      await createProductTypeApi(newProductType);

      setName("");
      setParentCategoryID("");
      setShow(true);
      setSuccessMessage("Tạo loại sản phẩm thành công");
      setErrorMessage("");
    } catch (error) {
      console.error("Error creating product type:", error);
      setSuccessMessage("");
      setErrorMessage("Tạo loại sản phẩm thất bại");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    navigate("/admin/producttype");
  };

  return (
    <div className="overflow-y-auto h-[calc(100vh-100px)] px-20 py-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/admin/producttype"
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeftOutlined className="text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Tạo mới loại sản phẩm
            </h1>
            <p className="text-gray-600 mt-1">
              Thêm loại sản phẩm mới vào hệ thống
            </p>
          </div>
        </div>

        {/* Success Modal */}
        <Modal show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Thành công</Modal.Title>
          </Modal.Header>
          <Modal.Body>Loại sản phẩm đã được thêm thành công!</Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleClose}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Thông tin loại sản phẩm
            </h2>

            <div className="space-y-6">
              {/* Product Type Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên loại sản phẩm <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Nhập tên loại sản phẩm..."
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    nameError ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {nameError && (
                  <p className="mt-1 text-sm text-red-600">{nameError}</p>
                )}
              </div>

              {/* Parent Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Danh mục cha
                </label>
                <select
                  value={parentCategoryID}
                  onChange={handleParentCategoryChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors bg-white"
                >
                  <option value="">Chọn danh mục cha (tùy chọn)</option>
                  {categories.map((category) => (
                    <option
                      key={category.categoryID}
                      value={category.categoryID}
                    >
                      {category.categoryID}
                    </option>
                  ))}
                </select>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{errorMessage}</p>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-600">{successMessage}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
            <div className="flex items-center justify-end gap-3">
              <Link
                to="/admin/producttype"
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Hủy
              </Link>
              <button
                type="button"
                onClick={handleCreate}
                disabled={isLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? "Đang tạo..." : "Tạo mới"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProductType;
